import { Component, HostListener, Input, OnDestroy, OnInit, TemplateRef } from "@angular/core"
import { Observable, Subject, Subscription } from "rxjs"
import { Language } from "../../../models/language.model"
import { TranscriptMedia } from "../../../models/resource/media"
import { Resource } from "../../../models/resource/resource"
import { StandardsInformation } from "../../../models/resource/standards"
import { ResourceDetailsService } from "../../../services/resource-details.service"
import { ResourceService } from "../../../services/resource.service"
import { UserService } from "../../../services/user.service"
import { IWindowResizeEvent, WindowSizeService } from "../../../services/window-size.service"
import { AddOnViewService } from "../../../services/add-on-view.service"
import Utils from "../../../utils/utils"
import { StudentGateService } from "../../../services/student-gate.service"
import { takeUntil, tap } from "rxjs/operators"
import { CautionFlagsService } from "../../../services/caution-flags.service"
import { Asset, ContentFlags } from "../../../models/resource/asset"
import { PreviewService } from "../../../services/preview.service"
import { LessonContent } from "../../../models/lessonContent.model"

@Component({
    selector: "app-resource-details",
    templateUrl: "./resource-details.component.html",
    styleUrls: ["./resource-details.component.scss"],
    standalone: false,
})
export class ResourceDetailsComponent implements OnInit, OnDestroy {
    readonly ABOUT = "About"
    readonly OVERVIEW = "Overview"
    readonly PROCEDURE = "Procedure"
    readonly STANDARDS = "Standards"
    readonly CAUTION_FLAGS = "Cautions"
    cautionTabAssetDescription: string = ""
    subscriptions: Subscription[] = []
    resource: Resource = null
    standardsInfo: StandardsInformation = null
    gtmPrefix: string = "gtm-resource-page"
    Language = Language
    tabList: string[] = []
    selectedTab: string = this.ABOUT
    window = window
    isAddonView: boolean
    isTeacherPreview: boolean
    brandSearchQueryParams: {}
    innerWidth: number
    isInPreviewMode$: Observable<boolean>
    private destroyed$: Subject<void> = new Subject<void>()

    @Input() public overviewTemplateRef: TemplateRef<any>
    @Input() public procedureTemplateRef: TemplateRef<any>

    cautionFlagTabToggleObservable: Observable<boolean> =
        this.cautionFlagsService.cautionFlagTabDisplayObservable.pipe(
            takeUntil(this.destroyed$),
            tap((nextState: boolean) => {
                if (nextState && this.resource) {
                    this.changeTab(this.CAUTION_FLAGS)
                }
            })
        )

    windowResizeObservable: Observable<IWindowResizeEvent> = this.windowSize.resize.pipe(
        takeUntil(this.destroyed$),
        tap((event: IWindowResizeEvent) => (this.innerWidth = event.innerWidth))
    )

    @HostListener("window:beforeunload", ["$event"])
    deleteActiveTabFromLocalStorage($event: unknown): void {
        this.resourceService.deleteActiveTab()
    }

    constructor(
        public userService: UserService,
        public windowSize: WindowSizeService,
        private resourceDetailsService: ResourceDetailsService,
        public resourceService: ResourceService,
        private addOnService: AddOnViewService,
        private studentGateService: StudentGateService,
        private cautionFlagsService: CautionFlagsService,
        private previewService: PreviewService
    ) {}

    public ngOnInit() {
        this.isInPreviewMode$ = this.previewService.previewOnResource$
        this.cautionFlagTabToggleObservable.subscribe()
        this.windowResizeObservable.subscribe()
        this.isAddonView = this.addOnService.isAddonView
        this.isTeacherPreview = this.addOnService.isTeacherPreview
        if (window.location.search.includes("?selected_tab=standards")) {
            this.selectedTab = this.STANDARDS
            history.replaceState({}, "?selected_tab=standards", window.location.pathname)
        }

        this.subscriptions.push(
            this.resourceService.resourceResponse.subscribe((resource: Resource) =>
                this.setNewProperties(resource)
            )
        )

        this.subscriptions.push(
            this.resourceService.standardsResponse.subscribe(
                (standardsInformation: StandardsInformation) => {
                    this.standardsInfo = standardsInformation
                    this.getTabsKeysForResourceType()
                }
            )
        )

        this.defineQueryParams()
    }

    setCautionTabDescription() {
        if (this.resourceService.activeTab === "Cautions") this.changeTab(this.CAUTION_FLAGS)
        if (this.resource.opensAsMediaGallery) {
            this.cautionTabAssetDescription = "The media in this gallery contain"
        } else if (this.resource.opensAsAudio) {
            this.cautionTabAssetDescription = "This audio recording contains"
        } else {
            this.cautionTabAssetDescription =
                "This " + this.resource.mediaType.toLowerCase() + " contains"
        }
    }

    assetCautionFlags(asset: Asset): ContentFlags[] {
        return asset.contentFlags
    }

    lessonPlanAssetCautionFlags(lessonContent: LessonContent): ContentFlags[] {
        return lessonContent.contentFlags
    }

    setLinkTargets(html: string): string {
        return this.isAddonView ? Utils.addTargetToHTMLLinks(html) : html
    }

    defineQueryParams() {
        let selected_facet: string[] = ["brand_id:" + this.resource.brand.id]
        if (this.userService.hasStudentExperience()) {
            if (this.studentGateService.selectedGradeValue) {
                selected_facet.push("grades:" + this.studentGateService.selectedGradeValue)
            }
        }
        this.brandSearchQueryParams = {
            q: "",
            selected_facet: [...selected_facet],
        }
    }

    redirectToResourceTypeView() {
        if (this.resourceService.focusView) {
            this.resourceService.switchResourceView(true)
        } else {
            this.resourceService.switchResourceView(true, true)
        }
    }

    public setNewProperties(resource: Resource) {
        this.resource = resource

        if (this.standardsInfo && this.resource.resourceCode !== this.standardsInfo.resourceCode) {
            this.standardsInfo = null
        }

        this.getTabsKeysForResourceType()
        if (this.resource.isLessonPlan && this.selectedTab === this.ABOUT) {
            this.selectedTab = this.OVERVIEW
        }

        this.setCautionTabDescription()
    }

    public getAccessibilities(): string {
        const accesibilities = []
        this.resource.accessibility.forEach((accesibility) => {
            accesibilities.push(...accesibility.indicators)
        })
        return accesibilities.join(", ")
    }

    public hasNextTab(): boolean {
        let activeIndex = null
        this.tabList.forEach((key: string, i: number) => {
            if (key === this.selectedTab) {
                activeIndex = i
            }
        })
        if (!activeIndex) activeIndex = 0
        return !!this.tabList[activeIndex + 1]
    }

    public hasPrevTab(): boolean {
        let activeIndex: number = 0
        this.tabList.forEach((key: string, i: number) => {
            if (key === this.selectedTab) {
                activeIndex = i
            }
        })
        return !!this.tabList[activeIndex - 1]
    }

    /**
     * gets the tab keys that are available for the resource
     */
    public getTabsKeysForResourceType() {
        this.tabList = [
            this.resource.isLessonPlan ? this.OVERVIEW : this.ABOUT,
            ...(this.resource.isLessonPlan && this.resource.lessonData.procedureData.length
                ? [this.PROCEDURE]
                : []),
            ...(this.hasStandards ? [this.STANDARDS] : []),
            ...(this.resource.shouldShowContentWarnings ? [this.CAUTION_FLAGS] : []),
        ]
    }

    public get hasStandards() {
        return (
            !!this.standardsInfo &&
            this.standardsInfo.hasDisplayableStandards() &&
            !this.userService.hasStudentExperience()
        )
    }

    public get hasContentFlags() {
        return this.resource.shouldShowContentWarnings
    }

    /**
     * Gets the next or previous tab key based on the currently active one, or the active one if 0 is provided
     * @param direction -1, 0, 1
     */
    public getTabKey(direction: number = 0): string {
        let activeIndex: number = 0
        if (
            (this.hasNextTab() && direction === 1) ||
            (this.hasPrevTab() && direction === -1) ||
            direction === 0
        ) {
            this.tabList.forEach((key: string, i: number) => {
                if (key === this.selectedTab) {
                    activeIndex = i
                }
            })
            return this.tabList[activeIndex + direction]
        }
    }

    public selectNextTab() {
        this.changeTab(this.getTabKey(1))
        this.scrollToResourceDetails()
    }

    public selectPrevTab() {
        this.changeTab(this.getTabKey(-1))
        this.scrollToResourceDetails()
    }

    public changeTab(tab: string): void {
        this.resourceService.activeTab = tab
        this.selectedTab = tab
    }

    public getNextTabName(): string {
        if (this.hasNextTab()) {
            return this.getTabKey(1)
        }
        return null
    }

    public getPrevTabName(): string {
        if (this.hasPrevTab()) {
            return this.getTabKey(-1)
        }
        return null
    }

    public isTabActive(tabName: string): boolean {
        return tabName === this.selectedTab
    }

    public download(event) {
        event.preventDefault()
        this.resourceDetailsService.showDownloadAgreement(
            this.resource?.selectedAsset?.selectedMedia
        )
    }

    public downloadTranscript(event) {
        const media: TranscriptMedia = this.resource.getSelectedMedia() as TranscriptMedia
        event.preventDefault()
        this.resourceService.downloadTranscript(media)
    }

    public changeLanguage(event) {
        this.resource.selectedAsset.selectLanguage(event.language)
    }

    private scrollToResourceDetails() {
        const topArea: Element = document.querySelector(".resource-details")
        topArea.scrollIntoView({
            behavior: "smooth",
            block: "start",
            inline: "nearest",
        })
    }

    public ngOnDestroy() {
        this.subscriptions.forEach((subscription: Subscription) => subscription.unsubscribe())
        this.resourceService.deleteActiveTab()
        this.destroyed$.next()
        this.destroyed$.complete()
    }
}
